import React from 'react';
import { Box, Grid, Typography, Button, TextField, Autocomplete } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Delete } from '@mui/icons-material';
import { useForm, Controller, SubmitErrorHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { SalesService } from 'services';
import dayjs from 'dayjs';
import { Sale } from 'models';
import { useQueryClient } from '@tanstack/react-query';
import { SaleFormProps } from './SaleFormProps';
import { useAuth, usePartners, useProducts, useUsers } from 'hooks';

const firstDayOfCurrentMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
const lastDayOfCurrentMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);

const saleSchema = z.object({
	id: z.number().optional(),
	timeStamp: z.coerce.date().min(firstDayOfCurrentMonth).max(lastDayOfCurrentMonth).optional(),
	partnerId: z.number().min(1, 'Select a partner'),
	userId: z.number().min(1, 'Select user'),
	productId: z.number().min(1, 'Select a product'),
});

export const SaleForm = ({ row }: SaleFormProps) => {
	const { isAdministrator, currentUserId } = useAuth();
	const isNew = (row?.id ?? 0) === 0;
	const { products } = useProducts();
	const { users } = useUsers();
	const { partners } = usePartners();
	const queryClient = useQueryClient();
	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<Sale>({
		defaultValues: row || {
			id: 0,
			partnerId: isAdministrator ? 0 : currentUserId,
			userId: 0,
			productId: 0,
			timeStamp: new Date()
		},
		resolver: zodResolver(saleSchema),
		mode: 'onBlur',
	});

	const onSubmit = async (data: Sale) => {
		if (isNew) {
			await SalesService.create(data);
		} else {
			await SalesService.update(data);
		}
		queryClient.invalidateQueries({ queryKey: ['sales'] });
	};

	const onErrorSubmit: SubmitErrorHandler<Sale> = async e => {
		console.log(e);
	};

	const handleDeleteSale = async () => {
		await SalesService.delete(row?.id ?? 0);
		queryClient.invalidateQueries({ queryKey: ['sales'] });
	};

	if (!users || !products || !partners) return <></>
	return (
		<Box sx={{ margin: 2 }} component='form' onSubmit={handleSubmit(onSubmit, onErrorSubmit)}>
			<Grid container spacing={2} pt={2}>
				<Typography variant='h6' gutterBottom component='div'>
					{`${isNew ? 'Create' : 'Edit'} Sale`}
				</Typography>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Controller
							name='timeStamp'
							control={control}
							render={({ field }) => (
								<DatePicker
									label='Date'
									value={field.value ? dayjs(field.value) : dayjs(new Date())}
									minDate={dayjs(firstDayOfCurrentMonth)}
									maxDate={dayjs(lastDayOfCurrentMonth)}
									onChange={newValue => {
										field.onChange(newValue?.toDate() ?? new Date());
									}}
									slotProps={{
										textField: {
											size: 'small',
											required: true,
											fullWidth: true,
										},
									}}
								/>
							)}
						/>
					</Grid>
					{isAdministrator && (
						<Grid item xs={12}>
							<Controller
								name='partnerId'
								control={control}
								render={({ field }) => (
									<Autocomplete
										value={partners?.find(partner => partner.id === field.value)}
										onChange={(_, value) => field.onChange(value?.id ?? 0)}
										options={partners || []}
										fullWidth
										getOptionLabel={option => option.firstName + ' ' + option.lastName}
										isOptionEqualToValue={(option) => option.id === field.value}
										renderInput={params => <TextField label='Partner' required error={!!errors.partnerId} helperText={errors.partnerId ? errors.partnerId.message : ''} {...params} size='small' />}
									/>
								)}
							/>
						</Grid>
					)}
					<Grid item xs={12}>
						<Controller
							name='userId'
							control={control}
							render={({ field }) => (
								<Autocomplete
									value={users?.find(user => user.id === field.value)}
									onChange={(_, value) => field.onChange(value?.id ?? 0)}
									options={users || []}
									fullWidth
									getOptionLabel={option => option.firstName + ' ' + option.lastName}
									isOptionEqualToValue={(option) => option.id === field.value}
									renderInput={params => <TextField label='User' required error={!!errors.userId} helperText={errors.userId ? errors.userId.message : ''} {...params} size='small' />}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12}>
						<Controller
							name='productId'
							control={control}
							render={({ field }) => (
								<Autocomplete
									value={products?.find(product => product.id === field.value)}
									onChange={(_, value) => field.onChange(value?.id ?? 0)}
									options={products || []}
									fullWidth
									getOptionLabel={option => option.name}
									isOptionEqualToValue={(option) => option.id === field.value}
									renderInput={params => <TextField label='Product' required error={!!errors.productId} helperText={errors.productId ? errors.productId.message : ''} {...params} size='small' />}
								/>
							)}
						/>
					</Grid>
					{isNew ? (
						<Grid item xs={12}>
							<Button type='submit' variant='contained' color='primary' fullWidth>
								Create
							</Button>
						</Grid>
					) : (
						<>
							<Grid item xs={10}>
								<Button type='submit' variant='contained' color='primary' fullWidth>
									Update
								</Button>
							</Grid>
							<Grid item xs={2}>
								<Button variant='contained' color='primary' fullWidth onClick={handleDeleteSale}>
									<Delete />
								</Button>
							</Grid>
						</>
					)}
				</Grid>
			</Grid>
		</Box>
	);
}
